import React, { useState, useRef } from "react";
import {
    Box,
    Text,
    Button,
    Flex,
    useColorModeValue,
    Modal,
    ModalContent,
    ModalOverlay,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import Pform from "./form";

const MotionBox = motion(Box);

const AboutUs: React.FC = () => {
    const { t } = useTranslation();
    const ref = useRef(null);

    // once: false → animation à l'entrée ET reset/disparition à la sortie
    const isInView = useInView(ref, { once: false, margin: "-100px" });

    const [showModal, setShowModal] = useState(false);

    const bg = useColorModeValue("#FAFAF7", "#0A1628");
    const textColor = useColorModeValue("#0A1628", "#E8E0D0");
    const accentColor = "#C9A84C";

    // Chaque mot du texte long apparaît / disparaît individuellement
    const words = t("aboutUsText").replace(/\s+/g, " ").trim().split(" ");

    const containerVariants = {
        hidden: {},
        visible: {
            transition: { staggerChildren: 0.022, delayChildren: 0.35 },
        },
    };

    const wordVariants = {
        hidden: {
            opacity: 0,
            y: 14,
            filter: "blur(4px)",
        },
        visible: {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
        },
    };

    const fadeUp = (delay = 0) => ({
        hidden: { opacity: 0, y: 28 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay },
        },
    });

    return (
        <Flex
            id="aboutUs"
            ref={ref}
            direction="column"
            align="center"
            justify="center"
            bg={bg}
            py={{ base: 28, md: 40 }}
            px={{ base: 8, md: 16 }}
            position="relative"
            overflow="hidden"
        >
            {/* Cercles décoratifs */}
            <Box
                position="absolute"
                top="-120px"
                right="-120px"
                w="400px"
                h="400px"
                borderRadius="full"
                border="1px solid"
                borderColor={useColorModeValue("rgba(10,22,40,0.05)", "rgba(255,255,255,0.04)")}
                pointerEvents="none"
            />
            <Box
                position="absolute"
                bottom="-80px"
                left="-80px"
                w="280px"
                h="280px"
                borderRadius="full"
                border="1px solid"
                borderColor={useColorModeValue("rgba(10,22,40,0.04)", "rgba(255,255,255,0.03)")}
                pointerEvents="none"
            />

            {/* Eyebrow */}
            <MotionBox
                variants={fadeUp(0)}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                mb={5}
            >
                <Text
                    fontSize="10px"
                    letterSpacing="0.35em"
                    textTransform="uppercase"
                    color={textColor}
                    opacity={0.35}
                    fontWeight="500"
                >
                    À propos
                </Text>
            </MotionBox>

            {/* Titre */}
            <MotionBox
                variants={fadeUp(0.1)}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                mb={8}
                textAlign="center"
            >
                <Text
                    fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
                    fontWeight="200"
                    textAlign="center"
                    color={textColor}
                    letterSpacing="-0.025em"
                    lineHeight="1.1"
                >
                    {t("aboutUsTitle").split(" ").map((word, i, arr) =>
                        i === arr.length - 1 ? (
                            <Box as="span" key={i} fontWeight="700">
                                {word}
                            </Box>
                        ) : (
                            `${word} `
                        )
                    )}
                </Text>
            </MotionBox>

            {/* Divider accent */}
            <MotionBox
                initial={{ scaleX: 0, opacity: 0 }}
                animate={isInView ? { scaleX: 1, opacity: 1 } : { scaleX: 0, opacity: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                style={{ originX: 0.5 }}
                mb={10}
            >
                <Box w="48px" h="1px" bg={accentColor} opacity={0.7} mx="auto" />
            </MotionBox>

            {/* Texte mot par mot */}
            <MotionBox
                maxW="680px"
                textAlign="center"
                mb={14}
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                <Text
                    as="p"
                    fontSize={{ base: "sm", md: "md" }}
                    color={textColor}
                    lineHeight="2"
                    fontWeight="300"
                    letterSpacing="0.01em"
                    // Nécessaire pour que les spans inline se comportent bien
                    sx={{ display: "block" }}
                >
                    {words.map((word, i) => (
                        <React.Fragment key={i}>
                            <motion.span
                                variants={wordVariants}
                                style={{
                                    display: "inline",
                                    whiteSpace: "pre",
                                }}
                            >
                                {word}
                            </motion.span>
                            {i < words.length - 1 ? " " : ""}
                        </React.Fragment>
                    ))}
                </Text>
            </MotionBox>

            {/* Bouton */}
            <MotionBox
                variants={fadeUp(0.45)}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
            >
                <Button
                    onClick={() => setShowModal(true)}
                    px={10}
                    py={6}
                    color={bg}
                    bg={textColor}
                    fontWeight="400"
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="transparent"
                    transition="all 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
                    _hover={{
                        bg: "transparent",
                        color: textColor,
                        borderColor: textColor,
                        transform: "translateY(-2px)",
                    }}
                    _active={{
                        transform: "translateY(0px)",
                        opacity: 0.8,
                    }}
                >
                    {t("navBarcontact")}
                </Button>
            </MotionBox>

            {/* Modal */}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} isCentered>
                <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(12px)" />
                <ModalContent
                    borderRadius="2xl"
                    overflow="hidden"
                    bg={useColorModeValue("white", "#0A1628")}
                >
                    <Pform />
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default AboutUs;